import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, FlatList, RefreshControl, Text, Pressable,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import { FontAwesome } from '@expo/vector-icons';
import isMobile from '../../../../components/IsMobile';
import NoTicketsImage from '../../../Tickets/components/NoTicketsImage';
import ScreenEmptyState from '../../../../components/ScreenEmptyState';
import Colors from '../../../../constants/Colors';
import TicketItem from './components/TicketItem';
import RefreshIcon from '../../../../components/16pxIcons/RefreshIcon';
import { Ticket } from '../../../../constants/Types';
import { TICKET_LIST_LOAD_FAILURE } from '../../../../constants/AlertMessages';
import PressableStyled, { PressableState } from '../../../../components/PressableStyled';
import Style from '../../../../constants/Style';
import SkeletonLoader from '../../../../components/SkeletonLoader';

interface TicketListProps {
  newReports: boolean,
  newCount: number,
  showSpinner: boolean,
  loading: boolean,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setRefreshing: Dispatch<SetStateAction<boolean>>,
  tickets: Ticket[],
  pinnedTickets: Ticket[],
  updatePinnedTickets: Dispatch<SetStateAction<Ticket[]>>,
  flatList,
  renderFilterTags,
  selectedTicket: Ticket | null,
  setModalVisible: Dispatch<SetStateAction<boolean>>,
  setSelectedTicket: Dispatch<SetStateAction<Ticket | null>>,
  originalFetchTime,
  listSize: number,
  count: number | undefined,
  loadTickets,
}

const TicketList = ({
  newReports, showSpinner, loadTickets, selectedTicket, setModalVisible, setSelectedTicket,
  originalFetchTime, listSize, count, tickets, flatList, renderFilterTags, loading, setLoading,
  setRefreshing, pinnedTickets, updatePinnedTickets, newCount,
}: TicketListProps) => {
  const [spin, setSpin] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [fetchTime, setFetchTime] = useState<number | null>(null);
  const [pinnedListExpanded, setPinnedListExpanded] = useState<boolean>(true);
  const selectedTicketId: string | undefined = selectedTicket?.id;

  useEffect(() => {
    if (!showSpinner) setSpin(false);
    if (!fetchTime) setFetchTime(originalFetchTime);
  }, [showSpinner]);

  useEffect(() => {
    if (!(selectedTicketId && tickets?.length)) return;
    const index: number = tickets.map((ticket: Ticket) => ticket.id).indexOf(selectedTicketId);
    if (index < 0 || index >= tickets?.length) return;
    const timeout = setTimeout(() => {
      if (flatList?.current && tickets?.length && index >= 0 && index < tickets.length) {
        flatList?.current?.scrollToIndex({ index, viewPosition: 0.5, animated: true });
      }
      return () => clearTimeout(timeout);
    }, 1000);
  }, [selectedTicketId, tickets]);

  useEffect(() => {
    loadTickets(pageNumber);
  }, [pageNumber]);

  useEffect(() => {
    setPageNumber(0);
  }, [count]);

  return <View
    style={styles.container}
    testID='ticket-list'
  >
    {newReports && <Pressable
      style={({ hovered }: PressableState) => [
        {
          backgroundColor: hovered ? '#fff' : Colors.light.hereworksBlue50,
          borderColor: hovered ? Colors.light.hereworksBlue300 : Colors.light.hereworksWhite200,
        },
        styles.newReportsContainer,
        Style.boxShadow,
      ]}
      onPress={() => {
        setLoading(true);
        setSpin(true);
      }}
    >
      <Animatable.View animation={spin ? 'rotate' : ''} iterationCount={'infinite'}>
        <RefreshIcon />
      </Animatable.View>
      <Text style={styles.newTicketsTitle}>
        {`${newCount} new tickets`}
      </Text>
    </Pressable>}
    {renderFilterTags()}
    {pinnedTickets?.length > 0 && <View style={styles.pinnedTicketsContainer}>
      <View style={Style.row}>
        <Text style={styles.sectionTitle}>{`Pinned (${pinnedTickets.length})`}</Text>
        <PressableStyled
          onPress={() => setPinnedListExpanded(!pinnedListExpanded)}
          style={Style.row}
        >
          <Text style={styles.sectionTitle}>{pinnedListExpanded ? 'Collapse' : 'Expand'}</Text>
          <FontAwesome
            name={pinnedListExpanded ? 'angle-up' : 'angle-down'}
            size={20}
            color={styles.sectionTitle.color}
          />
        </PressableStyled>
      </View>
      {pinnedListExpanded && pinnedTickets.map((ticket: Ticket, index: number) => <TicketItem
        {...{
          index,
          ticket,
          selectedTicketId,
          setModalVisible,
          setSelectedTicket,
          updatePinnedTickets,
          pinnedTickets,
        }}
        originalFetchTime={fetchTime}
        key={`ticket_item_${ticket.id}`}
      />)}
    </View>}
    {pinnedTickets?.length
      ? <Text style={[styles.sectionTitle, { marginLeft: 24, marginVertical: 12 }]}>
        {'All tickets'}
      </Text> : null}
    <FlatList
      getItemLayout={(_, index) => (
        { length: 134, offset: 134 * index, index }
      )}
      data={tickets?.filter((ticket: Ticket) => ticket.name?.toLowerCase()
        || ticket.id?.toLowerCase())}
      renderItem={({ item: ticket, index }) => <TicketItem
        {...{
          ticket,
          selectedTicketId,
          setModalVisible,
          setSelectedTicket,
          updatePinnedTickets,
          pinnedTickets,
          index,
        }}
        originalFetchTime={fetchTime}
      />}
      ref={flatList}
      onScrollToIndexFailed={() => { }}
      refreshControl={
        <RefreshControl
          refreshing={showSpinner}
          onRefresh={() => {
            setRefreshing(true);
            setLoading(true);
          }}
        />
      }
      ListFooterComponent={(count && tickets?.length) ? <FlatList
        horizontal
        testID='select-tickets-page'
        style={styles.pageNumbers}
        data={[...Array(Math.ceil(count / listSize)).keys()]}
        ListHeaderComponent={() => {
          const disabled: boolean = pageNumber <= 0;
          return <PressableStyled
            style={[
              styles.pageButton, styles.nextPageButton, disabled && styles.pageButtonDisabled,
            ]}
            onPress={() => setPageNumber(Math.max(0, pageNumber - 1))}
            onLongPress={() => setPageNumber(0)}
            {...{ disabled }}
            testID='previous-ticket-page-btn'
          >
            <FontAwesome
              name='angle-left'
              color={Colors.light.hereworksBlue500}
              size={20}
            />
          </PressableStyled>;
        }}
        ListFooterComponent={() => {
          const maxPageNumber: number = Math.ceil(count / listSize) - 1;
          const disabled: boolean = pageNumber >= maxPageNumber;
          return <PressableStyled
            style={[
              styles.pageButton, styles.nextPageButton, disabled && styles.pageButtonDisabled,
            ]}
            onPress={() => {
              setPageNumber(Math.min(pageNumber + 1, maxPageNumber));
            }}
            onLongPress={() => {
              setPageNumber(maxPageNumber);
            }}
            {...{ disabled }}
            testID='next-ticket-page-btn'
          >
            <FontAwesome
              name='angle-right'
              color={Colors.light.hereworksBlue500}
              size={20}
            />
          </PressableStyled>;
        }}
        renderItem={({ item }) => {
          if (Math.abs(item - pageNumber) > 2) return null;
          const selected: boolean = pageNumber === item;
          return <Pressable
            style={({ hovered, pressed }: PressableState) => [
              styles.pageButton, hovered && Style.hovered, pressed && Style.pressed,
              (hovered || pressed) && styles.pageButtonHovered,
              selected && styles.pageButtonSelected,
            ]}
            disabled={selected}
            onPress={() => setPageNumber(item)}
          >
            <Text style={[styles.pageButtonText, selected && styles.whiteText]}>{item + 1}</Text>
          </Pressable>;
        }}
        keyExtractor={(item: number) => `solve_analytics_tickets_page_number_${item + 1}`}
      /> : null}
      ListEmptyComponent={() => (loading ? <>
        <SkeletonLoader delay={50} />
        <SkeletonLoader delay={150} />
        <SkeletonLoader delay={200} />
        <SkeletonLoader delay={250} />
        <SkeletonLoader delay={300} />
        <SkeletonLoader delay={350} />
        <SkeletonLoader delay={400} />
        <SkeletonLoader delay={450} />
      </> : <ScreenEmptyState
        image={<NoTicketsImage />}
        title={tickets ? 'No tickets raised yet' : 'Error loading tickets'}
        subtitle={tickets
          ? 'You will receive a notification when someone reports a ticket'
          : TICKET_LIST_LOAD_FAILURE}
      />)}
      keyExtractor={(ticket: Ticket) => `ticket_${ticket.id}`}
      contentContainerStyle={styles.list}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    zIndex: -1,
    flex: 1,
    marginHorizontal: -24,
  },
  list: {
    paddingBottom: isMobile ? 24 : undefined,
    paddingHorizontal: 24,
  },
  newTicketsTitle: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 18,
    marginLeft: 5,
  },
  sectionTitle: {
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Roboto_500Medium',
    fontSize: 14,
    lineHeight: 18,
    marginEnd: 8,
  },
  newReportsContainer: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksLightBlue200,
    marginHorizontal: isMobile ? 20 : 0,
    marginTop: 8,
    padding: 8,
    borderRadius: 28,
    justifyContent: 'center',
    flexDirection: 'row',
    backgroundColor: Colors.light.plainBackground,
    position: 'absolute',
    zIndex: 1,
    alignSelf: 'center',
    top: -10,
  },
  newReportsContainerHovered: {
    backgroundColor: '#fff',
    borderColor: Colors.light.hereworksBlue300,
  },
  pageNumbers: {
    alignSelf: 'center',
    marginTop: 24,
  },
  pageButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 2,
  },
  nextPageButton: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    marginHorizontal: 8,
  },
  pageButtonSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  pageButtonDisabled: {
    opacity: 0.4,
  },
  pageButtonHovered: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
  },
  whiteText: {
    color: '#FFF',
  },
  pageButtonText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  pinnedTicketsContainer: {
    borderRadius: 16,
    padding: 10,
    backgroundColor: Colors.light.hereworksWhite100,
    marginHorizontal: 24,
    marginVertical: 12,
  },
});

export default TicketList;
